import { api, default as API_BASE_URL } from '../config/api';

export const proposalService = {
  // Obtener todas las propuestas
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.contact_id) queryParams.append('contact_id', params.contact_id);
    
    const queryString = queryParams.toString();
    const endpoint = `/proposals/${queryString ? `?${queryString}` : ''}`;
    return await api.get(endpoint);
  },

  // Obtener una propuesta por ID
  getById: async (id) => {
    return await api.get(`/proposals/${id}/`);
  },

  // Obtener una propuesta por proposal_id (V02000, etc.)
  getByProposalId: async (proposalId) => {
    return await api.get(`/proposals/by-proposal-id/${proposalId}/`);
  },

  // Crear una propuesta
  create: async (proposalData) => {
    return await api.post('/proposals/', proposalData);
  },

  // Actualizar una propuesta (PUT para actualizaciones completas)
  update: async (id, proposalData) => {
    return await api.put(`/proposals/${id}/`, proposalData);
  },

  // Actualizar parcialmente una propuesta (PATCH para cambios puntuales)
  partialUpdate: async (id, proposalData) => {
    return await api.patch(`/proposals/${id}/`, proposalData);
  },

  // Eliminar una propuesta
  delete: async (id) => {
    return await api.delete(`/proposals/${id}/`);
  },

  // Obtener estadísticas de propuestas
  getStats: async () => {
    return await api.get('/proposals/stats/');
  },

  // Obtener el último saludo utilizado
  getLastGreeting: async () => {
    const response = await api.get('/proposals/last-greeting/');
    return response.greeting || '';
  },

  // Generar token para compartir propuesta
  generateShareToken: async (proposalId) => {
    return await api.post(`/proposals/${proposalId}/generate-share-token/`);
  },

  // Obtener propuesta compartida por token (público, sin autenticación)
  getSharedProposal: async (shareToken) => {
    return await api.get(`/proposals/shared/${shareToken}/`, { skipAuth: true });
  },

  // Subir un archivo de factura
  uploadFile: async (proposalId, file, fileType = 'invoice') => {
    const formData = new FormData();
    formData.append('file', file);
    
    return await api.post(`/proposals/${proposalId}/upload-files/?file_type=${fileType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Descargar un archivo adjunto (factura)
  downloadFile: async (proposalId, fileType = 'invoice') => {
    const token = localStorage.getItem('token');
    // API_BASE_URL ya incluye /api, así que el endpoint no debe empezar con /api
    const url = `${API_BASE_URL}/proposals/${proposalId}/download-file/?file_type=${fileType}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      // Verificar si la respuesta es HTML (404 del servidor web)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        const text = await response.text();
        if (process.env.NODE_ENV === 'development') {
          console.error('Error 404 HTML recibido:', text.substring(0, 200));
        }
        throw new Error(`Endpoint no encontrado (404): ${url}. Verifica que el endpoint esté correcto.`);
      }
      throw new Error(`Error al descargar el archivo: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || `archivo_${fileType}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  },

  // Actualizar nota comercial
  updateCommercialNote: async (proposalId, commercialNote) => {
    const response = await api.put(`/proposals/${proposalId}/commercial-note/`, { commercial_note: commercialNote });
    // El backend devuelve { proposal: {...}, history: [...] }
    // api.put() devuelve directamente el JSON parseado
    console.log('[PROPOSAL SERVICE] Respuesta de updateCommercialNote:', response);
    
    // Verificar si response tiene la estructura esperada
    if (response && typeof response === 'object') {
      if (response.proposal) {
        return response.proposal;
      }
      if (response.data?.proposal) {
        return response.data.proposal;
      }
    }
    // Fallback: devolver los datos directamente
    return response;
  },

  // Obtener historial de nota comercial
  getCommercialNoteHistory: async (proposalId) => {
    try {
      // api.get() devuelve directamente el JSON parseado
      // El backend devuelve Response(serializer.data) que es un array directamente
      const response = await api.get(`/proposals/${proposalId}/commercial-note-history/`);
      
      console.log('[PROPOSAL SERVICE] Respuesta raw del historial:', response);
      console.log('[PROPOSAL SERVICE] Tipo:', typeof response, 'Es array:', Array.isArray(response));
      
      // El backend devuelve un array directamente, api.get() devuelve el JSON parseado
      // Por lo tanto, response debería ser directamente el array
      if (Array.isArray(response)) {
        console.log(`[PROPOSAL SERVICE] ✓ Historial recibido correctamente. ${response.length} registro(s)`);
        return response;
      }
      
      // Si no es un array, intentar extraerlo de diferentes lugares posibles
      if (response && typeof response === 'object') {
        if (Array.isArray(response.data)) {
          console.log(`[PROPOSAL SERVICE] Historial en response.data. ${response.data.length} registro(s)`);
          return response.data;
        }
        if (Array.isArray(response.history)) {
          console.log(`[PROPOSAL SERVICE] Historial en response.history. ${response.history.length} registro(s)`);
          return response.history;
        }
        // Buscar cualquier propiedad array
        for (const key in response) {
          if (Array.isArray(response[key])) {
            console.log(`[PROPOSAL SERVICE] Historial en response.${key}. ${response[key].length} registro(s)`);
            return response[key];
          }
        }
      }
      
      // Si llegamos aquí, la respuesta no tiene el formato esperado
      console.warn('[PROPOSAL SERVICE] ⚠️ Formato de respuesta inesperado:', response);
      return [];
    } catch (error) {
      console.error('[PROPOSAL SERVICE] Error obteniendo historial:', error);
      throw error;
    }
  },

};

